import React from "react";
import { Box, CardHeader, Typography, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Chart } from "./chart";
import { StandardCard } from "../../../../components/Cards";
import {
  CHART_STATUS_LABELS,
  getChartColors,
  IStatusCount,
  processWhatsappCounts,
} from "../../../market-campaign/constants/statusConfig";

interface StatusBarGraphProps {
  sx?: any;
  title: string;
  subheader: string;
  isLoading: boolean;
  statusCounts: IStatusCount[];
  selectedSeries: string[];
  onSeriesChange: (series: string[]) => void;
}

export const StatusBarGraph = ({
  sx,
  title,
  subheader,
  isLoading,
  statusCounts,
  selectedSeries,
  onSeriesChange,
  ...other
}: StatusBarGraphProps) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  // Transform the status counts data into chart data
  const transformStatusData = () => {
    // Process counts to group unknown statuses under FAILED
    const processedCounts = processWhatsappCounts(statusCounts);

    // Create a map of status counts for easy lookup
    const statusCountsMap = new Map<string, number>();
    processedCounts?.forEach((item) => {
      statusCountsMap.set(item.status, item.cnt);
    });

    // Use all status labels and get counts from the map (default to 0 if not present)
    const categories = CHART_STATUS_LABELS.map((item) => item.label);
    const seriesData = CHART_STATUS_LABELS.map(
      (item) => statusCountsMap.get(item.value) || 0
    );

    return {
      categories,
      series: [
        {
          name: "Message Status",
          data: seriesData,
        },
      ],
    };
  };

  const chartData = transformStatusData();

  const chartOptions = {
    colors: chartColors,
    plotOptions: {
      bar: {
        distributed: true,
        borderRadius: 4,
        horizontal: false,
        columnWidth: "40%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: chartData.categories,
      labels: {
        style: {
          colors: theme.palette.text.secondary,
          fontSize: "10px",
        },
        rotate: -45,
        rotateAlways: false,
        maxHeight: 60,
      },
      tickPlacement: "on",
    },
    yaxis: {
      labels: {
        style: {
          colors: theme.palette.text.secondary,
        },
      },
    },
    tooltip: {
      y: {
        formatter: (val: number) => `${val} messages`,
      },
    },
    legend: {
      show: false,
    },
    grid: {
      strokeDashArray: 4,
    },
    responsive: [
      {
        breakpoint: 1200,
        options: {
          xaxis: {
            labels: {
              rotate: -45,
              fontSize: "11px",
            },
          },
        },
      },
      {
        breakpoint: 768,
        options: {
          xaxis: {
            labels: {
              rotate: -90,
              fontSize: "10px",
            },
          },
          plotOptions: {
            bar: {
              columnWidth: "50%",
            },
          },
        },
      },
      {
        breakpoint: 480,
        options: {
          xaxis: {
            labels: {
              rotate: -90,
              fontSize: "9px",
            },
          },
          plotOptions: {
            bar: {
              columnWidth: "60%",
            },
          },
        },
      },
    ],
  };

  return (
    <StandardCard sx={sx} {...other}>
      <CardHeader title={title} subheader={subheader} />

      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          pb: { xs: 1, sm: 1 },
          height: { xs: 280, sm: 320, md: 350 },
        }}
      >
        <Chart
          dir="ltr"
          type="bar"
          series={chartData.series}
          options={chartOptions}
          width="100%"
          height="100%"
        />
      </Box>

      <Stack
        direction="row"
        spacing={2}
        sx={{
          px: 3,
          py: 2,
          flexWrap: "wrap",
          gap: 1,
          justifyContent: { xs: "center", sm: "flex-end" },
        }}
      >
        {chartData.categories.map((category, index) => (
          <Stack
            key={category}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              minWidth: { xs: "auto", sm: "fit-content" },
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                bgcolor: chartColors[index % chartColors.length],
                borderRadius: 0.5,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                whiteSpace: "nowrap",
              }}
            >
              {category}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </StandardCard>
  );
};
